// @ts-expect-error Missing types
import jsreport from '@jsreport/browser-client';
import { type SILogReport } from '@/reports/models/report';
import { type ReportServerConfig } from '@/reports/models/report';
import MovedProductsReportFilter from './MovedProductsReportFilter.vue';
import { ReportType } from 'shared/reports/domain/reportType';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useHttpClient } from '@/common/api/httpClient';
import { useDarkMode } from 'shared/themes/composables/darkMode';
import { useThemeStore } from 'shared/themes/stores/themeStore';

interface ReportData {
  darkMode: boolean;
  tiYardTheme: boolean;
  products: ReportDataProducts[];
}

interface ReportDataProducts {
  productName: string;
  numberOfMoves: number;
  numberOfLogs: number;
  volume: string;
  timeSpent: string;
  averageFillLevel: string;
}

interface ProductData {
  productName: string;
  numberOfMoves: number;
  numberOfLogs: number;
  volume: number;
  timeSpent: number;
  averageFillLevel: number;
}

interface LogYardMovedProductsRequest {
  from: string;
  to: string;
  productIds: string[];
}

const REPORT_TYPE = ReportType.LogYardMovedProducts;
const TEMPLATE_NAME = 'log_yard_moved_products';

export function useMovedProductsReport(): SILogReport {
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  async function fetchDataFromServer(config: ReportServerConfig): Promise<ReportData | null> {
    const httpClient = useHttpClient();
    const headers = httpClient.defaultHeaders();

    // Get date range from filter values or use default
    const filterValues = config.filterValues as
      | { from: string; to: string; productIds: string[] }
      | undefined;
    if (!filterValues?.from || !filterValues?.to || !filterValues?.productIds?.length) {
      throw new Error('Filter values "from", "to" and "productIds" are required');
    }

    const body: LogYardMovedProductsRequest = {
      from: filterValues.from,
      to: filterValues.to,
      productIds: filterValues.productIds,
    };

    const request = new Request(httpClient.siteUrl(`report/${REPORT_TYPE}`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const response = await httpClient.execute(request);

    if (response.ok) {
      const reportDataFromServer = (await response.json()) as ProductData[];

      jsreport.serverUrl = config.reportUrl;
      if (config.authorization) {
        jsreport.headers['Authorization'] = config.authorization;
      }

      const darkMode = useDarkMode();
      const { enabled } = storeToRefs(darkMode);
      const themeStore = useThemeStore();
      const theme = themeStore.currentTheme;

      const reportData: ReportData = {
        darkMode: enabled.value,
        tiYardTheme: theme === 'TiYard',
        products: [],
      };

      for (const product of reportDataFromServer) {
        reportData.products.push({
          productName: product.productName,
          numberOfMoves: product.numberOfMoves,
          numberOfLogs: product.numberOfLogs,
          volume: product.volume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          timeSpent: product.timeSpent.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          averageFillLevel: product.averageFillLevel.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
        });
      }

      reportData.products.sort((a, b) =>
        a.productName.localeCompare(b.productName, languageStore.language.code, {
          numeric: true,
          sensitivity: 'base',
        }),
      );

      return reportData;
    }

    return null;
  }

  return {
    name: translations.value.reports.reportName(REPORT_TYPE),
    type: REPORT_TYPE,

    exportFormats: [
      {
        label: translations.value.reports.exportFormat.pdf,
        icon: 'pi pi-file-pdf',
        format: 'pdf',
      },
      {
        label: translations.value.reports.exportFormat.excel,
        icon: 'pi pi-file-excel',
        format: 'excel',
      },
    ],

    filterComponent: MovedProductsReportFilter,

    async showReport(config: ReportServerConfig | null): Promise<string> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: 'html',
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          let url = await report.toDataURI();
          url = url.replace(';base64,', ';charset=utf-8;base64,');
          return url;
        } catch (error) {
          console.error('Error generating report:', error);
          return '';
        }
      }

      return '';
    },

    async exportReport(config: ReportServerConfig | null, format: string): Promise<boolean> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      let recipe = 'html';
      let extension = '.html';
      if (format === 'pdf') {
        recipe = 'chrome-pdf';
        extension = 'pdf';
      } else if (format === 'excel') {
        recipe = 'html-to-xlsx';
        extension = 'xlsx';
      }

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: recipe,
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          report.download(`${TEMPLATE_NAME}.${extension}`);
          return true;
        } catch (error) {
          console.error('Error exporting report:', error);
        }
      }

      return false;
    },
  };
}
